import { Grid, Typography } from '@material-ui/core';
import LabelIcon from '@material-ui/icons/LocalOffer';
import StatSection from './StatSection';
import ColumnChart from './ColumnChart';
import { RATE_BAR_COLOR } from './palette';
import tagLabel from '../../../utils/tagLabels';

// The tags of the tasks the contestant solved, against how often a task carrying
// that tag came up on the boards they played on. "4 / 9" is four solved of the
// nine that came up - a task counts once per board it appeared on, and counts
// whoever solved it, or nobody.
//
// Two orders of the same columns: the tags they solved the most of, and the tags
// they solved the largest share of. The second one lets a tag that came up twice
// and was solved twice outrank one solved ten times out of thirty, so ties on the
// share are broken by how often the tag came up.
function TagsCard(props) {
    const tags = props.tags ?? [];

    const rateOf = tag => tag.occurrences === 0 ? 0 : tag.count / tag.occurrences;
    // a name too long for a column is shortened for the chart; the tooltip still
    // carries it in full
    const labelFor = tagLabel;
    const byRate = [...tags].sort((lhs, rhs) => {
        if (rateOf(lhs) !== rateOf(rhs)) return rateOf(rhs) - rateOf(lhs);
        if (lhs.occurrences !== rhs.occurrences) return rhs.occurrences - lhs.occurrences;
        return lhs.tag < rhs.tag ? -1 : 1;
    });

    // one stat to a line
    const tooltipFor = tag => (
        <>
            <div>{`${tag.tag}: ${tag.count} Solves / ${tag.occurrences} Occurrences (${(rateOf(tag) * 100).toFixed(0)}%)`}</div>
            <div>{`${tag.tag} take up ${tag.percentage.toFixed(0)}% of total solves`}</div>
            {tag.attempts != null && <div>{`${tag.attempts.toFixed(2)} submissions per solve`}</div>}
        </>
    );

    // Ten tags at most, and no further than the first tag they never solved
    const take = ordered => {
        const end = ordered.findIndex(tag => tag.count === 0);
        return ordered.slice(0, end === -1 ? 10 : Math.min(end, 10));
    };

    const bySolves = take(tags).map(tag => ({
        key: tag.tag,
        label: labelFor(tag.tag),
        value: tag.count,
        valueLabel: `${tag.count} / ${tag.occurrences}`,
        note: tag.attempts == null ? null : `${tag.attempts.toFixed(1)} att/solve`,
        tooltip: tooltipFor(tag)
    }));
    const bySolveRate = take(byRate).map(tag => ({
        key: tag.tag,
        label: labelFor(tag.tag),
        value: rateOf(tag),
        valueLabel: `${(rateOf(tag) * 100).toFixed(0)}%`,
        note: `${tag.count} / ${tag.occurrences}`,
        color: RATE_BAR_COLOR,
        tooltip: tooltipFor(tag)
    }));

    return (
        <StatSection
            icon={<LabelIcon />}
            title='Top Tags'
        >
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Typography variant='body2' color='textSecondary' gutterBottom> By solves </Typography>
                    <ColumnChart items={bySolves} emptyMessage='No tag has been recorded for the solved tasks yet' />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography variant='body2' color='textSecondary' gutterBottom> By solve rate </Typography>
                    <ColumnChart items={bySolveRate} emptyMessage='No tag has been recorded for the solved tasks yet' />
                </Grid>
            </Grid>
        </StatSection>
    );
}

export default TagsCard;
